import React from 'react';
import styled, { css } from 'styled-components';
import { Classable, Entity, EntityTypeName, EntityWithPublished, Shapeable } from '@shapeable/types';
import { breakpoints, theme } from '@shapeable/theme';
import { classNames, entityTypeNameFor, formatDateString, PLATFORM_DATE_FORMAT } from '@shapeable/utils';
import { EntityCard, useEntityTypeCards, useLang, useMonthNames } from '@shapeable/ui';
import { motion, useReducedMotion } from 'framer-motion';
import { EntityPublicationCard } from './entity-publication-card';
import { CalendarIconGlyph } from '@shapeable/icons';

const cls = classNames('entity-publication-timeline-item');

const LINE_COLOR = theme.COLOR('line-subtle');

const CHROME_SPRING = {
  type: 'spring',
  stiffness: 460,
  damping: 32,
  mass: 0.7,
};

const CARD_SPRING = {
  type: 'spring',
  stiffness: 400,
  damping: 30,
  mass: 0.85,
};

const HOVER_SPRING = {
  type: 'spring',
  stiffness: 520,
  damping: 32,
  mass: 0.55,
};

const dateRowVariants = {
  hidden: { opacity: 0, x: -12 },
  visible: { opacity: 1, x: 0 },
  hover: { opacity: 1, x: 4 },
};

const dateLabelVariants = {
  hidden: { opacity: 0, y: 6 },
  visible: { opacity: 1, y: 0 },
  hover: { opacity: 1, y: 0, x: 2 },
};

const markerVariants = {
  hidden: { opacity: 0, scale: 0.82 },
  visible: { opacity: 1, scale: 1 },
  hover: { opacity: 1, scale: 1.08 },
};

// -------- Props -------->

export type EntityPublicationTimelineItemProps = Classable & {
  entity: Entity;
  isLast?: boolean;
  /** Stagger offset passed from the grid — only runs on mount for entering rows */
  enterDelay?: number;
};

// -------- Child Component Props -------->

type ItemProps = {
  $isLast?: boolean;
};

type RailProps = {
  $hasDate: boolean;
  $isLast?: boolean;
};

type DateRowProps = {
  $isCardHovered?: boolean;
};

type MarkerComponentProps = {
  $isCardHovered?: boolean;
};

// -------- Styles -------->

const ItemStyles = breakpoints({
  base: css`
    display: flex;
    flex-direction: column;
    width: 100%;
    position: relative;
    overflow: visible;
    padding-bottom: ${theme.UNIT(4)};

    ${({ $isLast }: ItemProps) => $isLast && css`
      padding-bottom: 0;
    `}
  `,
  tablet: css`
    display: grid;
    grid-template-columns: ${theme.UNIT(8)} 1fr;
    column-gap: ${theme.UNIT(3)};
    padding-bottom: ${theme.UNIT(6)};
  `,
});

const RailStyles = breakpoints({
  base: css`
    display: none;
  `,
  tablet: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;

    ${({ $hasDate, $isLast }: RailProps) => $hasDate && css`
      &::after {
        content: '';
        position: absolute;
        top: ${theme.UNIT(8)};
        bottom: ${theme.UNIT(-6)};
        left: 50%;
        width: 1px;
        transform: translateX(-50%);
        background: ${LINE_COLOR};
        pointer-events: none;
      }
    `}

    ${({ $isLast }: RailProps) => $isLast && css`
      &::after {
        display: none;
      }
    `}
  `,
});

const MarkerStyles = breakpoints({
  base: css`
    display: none;
  `,
  tablet: css`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: ${theme.UNIT(8)};
    height: ${theme.UNIT(8)};
    border-radius: 50%;
    border: 1px solid ${({ $isCardHovered }: MarkerComponentProps) => (
      $isCardHovered ? theme.COLOR('primary') : LINE_COLOR
    )};
    background: ${theme.COLOR('bright')};
    color: ${({ $isCardHovered }: MarkerComponentProps) => (
      $isCardHovered ? theme.COLOR('primary') : theme.COLOR('text-subtle')
    )};
    position: relative;
    z-index: 1;
    ${theme.TRANSITION('border-color', 'color')};
  `,
});

const BodyStyles = breakpoints({
  base: css`
    display: flex;
    flex-direction: column;
    min-width: 0;
    flex: 1;
  `,
});

const DateRowStyles = breakpoints({
  base: css`
    display: flex;
    align-items: center;
    gap: ${theme.UNIT(2)};
    margin-bottom: ${theme.UNIT(3)};
    font-weight: 500;
    font-size: ${theme.FONT_SIZE(14)};
    color: ${({ $isCardHovered }: DateRowProps) => (
      $isCardHovered ? theme.COLOR('strong') : theme.COLOR('text-subtle')
    )};
    ${theme.TRANSITION('color')};
  `,
  tablet: css`
    position: relative;
    margin-bottom: ${theme.UNIT(3)};
    padding: ${theme.UNIT(1.5)} 0 ${theme.UNIT(1.5)} 4px;

    &::before {
      content: '';
      position: absolute;
      right: 100%;
      top: 50%;
      width: ${({ $isCardHovered }: DateRowProps) => (
        $isCardHovered ? theme.UNIT(4) : theme.UNIT(3)
      )};
      height: 1px;
      background: ${({ $isCardHovered }: DateRowProps) => (
        $isCardHovered ? theme.COLOR('primary') : LINE_COLOR
      )};
      transform: translateY(-50%);
      ${theme.TRANSITION('width', 'background')};
    }
  `,
});

const MobileMarkerStyles = breakpoints({
  base: css`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: ${theme.UNIT(7)};
    height: ${theme.UNIT(7)};
    border-radius: 50%;
    border: 1px solid ${({ $isCardHovered }: MarkerComponentProps) => (
      $isCardHovered ? theme.COLOR('primary') : LINE_COLOR
    )};
    background: ${theme.COLOR('bright')};
    color: ${({ $isCardHovered }: MarkerComponentProps) => (
      $isCardHovered ? theme.COLOR('primary') : theme.COLOR('text-subtle')
    )};
    ${theme.TRANSITION('border-color', 'color')};
  `,
  tablet: css`
    display: none;
  `,
});

const CardWrapStyles = breakpoints({
  base: css`
    width: 100%;
  `,
});

const DateLabelStyles = breakpoints({
  base: css`
    display: inline-block;
  `,
});

// -------- Components -------->

const My = {
  Item: styled.div<ItemProps>`${ItemStyles}`,
  Rail: styled.div<RailProps>`${RailStyles}`,
  Marker: styled(motion.div)<MarkerComponentProps>`${MarkerStyles}`,
  MobileMarker: styled(motion.div)<MarkerComponentProps>`${MobileMarkerStyles}`,
  Body: styled.div`${BodyStyles}`,
  DateRow: styled(motion.div)<DateRowProps>`${DateRowStyles}`,
  DateLabel: styled(motion.span)`${DateLabelStyles}`,
  CardWrap: styled(motion.div)`${CardWrapStyles}`,
  Card: EntityPublicationCard,
};

export const EntityPublicationTimelineItem: Shapeable.FC<EntityPublicationTimelineItemProps> = (props) => {
  const { className, entity, isLast = false, enterDelay } = props;
  const prefersReducedMotion = useReducedMotion();
  const [isCardHovered, setIsCardHovered] = React.useState(false);
  const t = useLang();
  const monthNames = useMonthNames();

  const ENTITY_CARD_COMPONENTS = useEntityTypeCards();

  const { published } = entity as EntityWithPublished;
  const formattedDate = published
    ? formatDateString(published.slice(0, 10), t('D MMMM YYYY'), PLATFORM_DATE_FORMAT, { monthNames })
    : '';

  const hasDate = !!formattedDate;
  const shouldAnimate = enterDelay !== undefined && !prefersReducedMotion;
  const enableHoverChrome = hasDate && !prefersReducedMotion;
  const chromeAnimate = enableHoverChrome && isCardHovered ? 'hover' : 'visible';

  const markerDelay = enterDelay ?? 0;
  const dateDelay = markerDelay + 0.05;
  const cardDelay = markerDelay + (hasDate ? 0.1 : 0.04);

  const markerTransition = chromeAnimate === 'hover'
    ? HOVER_SPRING
    : { ...CHROME_SPRING, delay: markerDelay };

  const dateTransition = chromeAnimate === 'hover'
    ? HOVER_SPRING
    : { ...CHROME_SPRING, delay: dateDelay };

  const dateLabelTransition = chromeAnimate === 'hover'
    ? HOVER_SPRING
    : { ...CHROME_SPRING, delay: dateDelay + 0.03 };

  const cardMotion = shouldAnimate
    ? {
        initial: { opacity: 0, y: 22, scale: 0.98 },
        animate: { opacity: 1, y: 0, scale: 1 },
        transition: { ...CARD_SPRING, delay: cardDelay },
      }
    : {
        initial: false,
        animate: { opacity: 1, y: 0, scale: 1 },
      };

  const CardComponent = ENTITY_CARD_COMPONENTS[entityTypeNameFor(entity) as EntityTypeName] || EntityCard;

  const markerIconColors = {
    fill: isCardHovered && enableHoverChrome ? 'primary' : 'text-subtle',
  } as const;

  return (
    <My.Item className={cls.name(className)} $isLast={isLast}>
      <My.Rail $hasDate={hasDate} $isLast={isLast}>
        {hasDate && (
          <My.Marker
            $isCardHovered={isCardHovered && enableHoverChrome}
            variants={markerVariants}
            initial={shouldAnimate ? 'hidden' : 'visible'}
            animate={chromeAnimate}
            transition={markerTransition}
            style={{ transformOrigin: 'center center' }}
          >
            <CalendarIconGlyph size={15} colors={markerIconColors} />
          </My.Marker>
        )}
      </My.Rail>

      <My.Body>
        {hasDate && (
          <My.DateRow
            $isCardHovered={isCardHovered && enableHoverChrome}
            variants={dateRowVariants}
            initial={shouldAnimate ? 'hidden' : 'visible'}
            animate={chromeAnimate}
            transition={dateTransition}
          >
            <My.MobileMarker
              $isCardHovered={isCardHovered && enableHoverChrome}
              variants={markerVariants}
              initial={shouldAnimate ? 'hidden' : 'visible'}
              animate={chromeAnimate}
              transition={markerTransition}
              style={{ transformOrigin: 'center center' }}
            >
              <CalendarIconGlyph size={15} colors={markerIconColors} />
            </My.MobileMarker>
            <My.DateLabel
              variants={dateLabelVariants}
              initial={shouldAnimate ? 'hidden' : 'visible'}
              animate={chromeAnimate}
              transition={dateLabelTransition}
            >
              {formattedDate}
            </My.DateLabel>
          </My.DateRow>
        )}

        <My.CardWrap
          {...cardMotion}
          onHoverStart={enableHoverChrome ? () => setIsCardHovered(true) : undefined}
          onHoverEnd={enableHoverChrome ? () => setIsCardHovered(false) : undefined}
        >
          <CardComponent entity={entity} />
        </My.CardWrap>
      </My.Body>
    </My.Item>
  );
};

EntityPublicationTimelineItem.cls = cls;
